/**
 * Utility functions for transforming alarm data from API response
 */

interface AlarmDataByName {
  [name: string]: {
    x: string[];
    y: number[];
  };
}

interface TransformedAlarmData extends Record<string, unknown> {
  alarms: {
    id: string;
    title: string;
    enabled: boolean;
    unit: string;
    splitToggle: boolean;
    isDiscrete: boolean;
    splitCol: number;
    legend: Array<{
      displayName: string;
      id: string;
      severity: string;
    }>;
    colors: string[];
    sensorUiNames: string[];
  };
  data: {
    data: Array<{
      displayName: string;
      name: string;
      x: string[];
      y: number[];
      type: string;
      mode: string;
      marker: {
        color: string;
      };
      isAlarm: boolean;
      alarmMetadata: {
        severity: string;
        priority: string;
        alarmCode: string;
        alarmState: string;
        component: string;
      };
    }>;
    legend: Array<{
      displayName: string;
    }>;
  };
}

const ALARM_COLORS = ['#ef4444', '#f97316', '#eab308', '#3b82f6', '#8b5cf6', '#ec4899'];

/**
 * Creates an empty alarm data structure
 */
export function createEmptyAlarmData(): TransformedAlarmData {
  return {
    alarms: {
      id: 'alarms',
      title: 'Alarms',
      enabled: true,
      unit: 'Count',
      splitToggle: false,
      isDiscrete: true,
      splitCol: 1,
      legend: [],
      colors: [],
      sensorUiNames: [],
    },
    data: {
      data: [],
      legend: [],
    },
  };
}

/**
 * Transforms alarm response data from API into chart-compatible format
 */
export function transformAlarmsData(alarmsResponse: any): TransformedAlarmData {
  // Return empty structure if no data
  if (!alarmsResponse || !Array.isArray(alarmsResponse) || alarmsResponse.length === 0) {
    return createEmptyAlarmData();
  }


  // Extract unique alarm names and organize data
  const uniqueAlarmNames = new Set<string>();
  const alarmDataByName: AlarmDataByName = {};

  // Handle different API response formats
  if (alarmsResponse[0]?.alarm_code) {
    // Format 1: Array of alarm objects with alarm_code, count, etc.
    processIndividualAlarmObjects(alarmsResponse, uniqueAlarmNames, alarmDataByName);
  } else if (alarmsResponse[0]?.names) {
    // Format 2: Aggregated format with names and values arrays
    processAggregatedAlarmData(alarmsResponse, uniqueAlarmNames, alarmDataByName);
  } else {
    // Generic format handler
    processGenericAlarmData(alarmsResponse, uniqueAlarmNames, alarmDataByName);
  }

  const alarmNamesArray = Array.from(uniqueAlarmNames);


  return buildTransformedData(alarmNamesArray, alarmDataByName);
}

/**
 * Process individual alarm objects format
 */
function processIndividualAlarmObjects(
  alarmsResponse: any[],
  uniqueAlarmNames: Set<string>,
  alarmDataByName: AlarmDataByName & { metadata?: Record<string, any> }
): void {
  alarmsResponse.forEach((alarm: any) => {
    const name = alarm.alarm_code || alarm.name || 'Unknown';
    uniqueAlarmNames.add(name);
    
    if (!alarmDataByName[name]) {
      alarmDataByName[name] = { x: [], y: [] };
    }
    
    // Store metadata if available
    if (alarm.metadata && !alarmDataByName.metadata) {
      alarmDataByName.metadata = {};
    }
    if (alarm.metadata) {
      alarmDataByName.metadata![name] = alarm.metadata;
    }
    
    alarmDataByName[name].x.push(alarm.timestamp || alarm.date || new Date().toISOString());
    alarmDataByName[name].y.push(alarm.count || alarm.value || 1);
  });
}

/**
 * Process aggregated alarm data format
 */
function processAggregatedAlarmData(
  alarmsResponse: any[],
  uniqueAlarmNames: Set<string>,
  alarmDataByName: AlarmDataByName
): void {
  alarmsResponse.forEach((entry: any) => {
    if (entry.names && Array.isArray(entry.names)) {
      entry.names.forEach((name: string, idx: number) => {
        uniqueAlarmNames.add(name);
        
        if (!alarmDataByName[name]) {
          alarmDataByName[name] = { x: [], y: [] };
        }
        
        alarmDataByName[name].x.push(entry.date);
        alarmDataByName[name].y.push(entry.values?.[idx] || 0);
      });
    }
  });
}

/**
 * Process generic alarm data format
 */
function processGenericAlarmData(
  alarmsResponse: any[],
  uniqueAlarmNames: Set<string>,
  alarmDataByName: AlarmDataByName
): void {
  alarmsResponse.forEach((item: any) => {
    const name = item.alarm_code || item.code || item.name || item.alarm_name || 'Unknown';
    const value = item.count || item.value || item.alarm_count || 1;
    const date = item.date || item.timestamp || item.created_at || new Date().toISOString();
    
    uniqueAlarmNames.add(name);
    
    if (!alarmDataByName[name]) {
      alarmDataByName[name] = { x: [], y: [] };
    }
    
    alarmDataByName[name].x.push(date);
    alarmDataByName[name].y.push(value);
  });
}

/**
 * Build the final transformed data structure
 */
function buildTransformedData(
  alarmNamesArray: string[],
  alarmDataByName: AlarmDataByName & { metadata?: Record<string, any> }
): TransformedAlarmData {
  const alarmMetadata = alarmDataByName.metadata || {};
  
  return {
    alarms: {
      id: 'alarms',
      title: 'Alarms',
      enabled: true,
      unit: 'Count',
      splitToggle: false,
      isDiscrete: true,
      splitCol: 1,
      legend: alarmNamesArray.map((name: string, index: number) => {
        const metadata = alarmMetadata[name] || {};
        return {
          displayName: name,
          id: `alarm_${index}`,
          name: name,
          // Include metadata in legend items
          severity: metadata.severity || 'warning',
          priority: metadata.priority || 'medium',
          interface: metadata.alarm_interface || metadata.interface || '',
        };
      }),
      colors: alarmNamesArray.map((_, index: number) => 
        ALARM_COLORS[index % ALARM_COLORS.length]
      ),
      sensorUiNames: alarmNamesArray,
    },
    data: {
      data: alarmNamesArray.map((name: string, index: number) => {
        const metadata = alarmMetadata[name] || {};
        return {
          displayName: name,
          name: name,
          x: alarmDataByName[name]?.x || [],
          y: alarmDataByName[name]?.y || [],
          type: 'bar',
          mode: 'markers',
          marker: {
            color: ALARM_COLORS[index % ALARM_COLORS.length],
          },
          isAlarm: true,
          alarmMetadata: {
            severity: metadata.severity || 'warning',
            priority: metadata.priority || 'medium',
            alarmCode: name,
            alarmState: metadata.alarm_state || 'active',
            component: metadata.component || 'Unknown',
            interface: metadata.alarm_interface || metadata.interface || '',
          },
        };
      }),
      legend: alarmNamesArray.map((name: string) => ({
        displayName: name,
      })),
    },
  };
}
